import { useState, useEffect, useMemo, useCallback } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Copy, CheckCircle2, ExternalLink, QrCode, AlertCircle, Info, Loader2 } from 'lucide-react';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';

// QR Code cache - stores generated QR codes by address
const qrCodeCache = new Map<string, string>();

// Use qrcode library (available via transitive dependencies)
// Use a function-scoped variable to avoid TDZ issues
const loadQRCode = async () => {
  try {
    // Try to import qrcode from node_modules
    const QRCodeModule = await import('qrcode');
    return QRCodeModule.default;
  } catch (error) {
    console.warn('Failed to load qrcode library:', error);
    // Fallback: return a mock that generates a placeholder
    return {
      toDataURL: async () => {
        return 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMzAwIiBoZWlnaHQ9IjMwMCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48cmVjdCB3aWR0aD0iMzAwIiBoZWlnaHQ9IjMwMCIgZmlsbD0iI2YzZjRmNiIvPjx0ZXh0IHg9IjUwJSIgeT0iNTAlIiBmb250LWZhbWlseT0iQXJpYWwiIGZvbnQtc2l6ZT0iMTQiIGZpbGw9IiM2YjcyODAiIHRleHQtYW5jaG9yPSJtaWRkbGUiIGR5PSIuM2VtIj5RUiBDb2RlIFVuYXZhaWxhYmxlPC90ZXh0Pjwvc3ZnPg==';
      },
    };
  }
};

interface DepositAddressModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  depositAddress: string;
  amount: number;
  token: string;
  exchangeId?: string;
  provider: string;
  destinationAddress?: string;
  toToken?: string;
  payinExtraId?: string; // Memo/tag for memo-based coins (XRP, XLM, etc.)
}

function DepositAddressModal({
  open,
  onOpenChange,
  depositAddress,
  amount,
  token,
  exchangeId,
  provider,
  destinationAddress,
  toToken,
  payinExtraId,
}: DepositAddressModalProps) {
  const [qrCodeDataUrl, setQrCodeDataUrl] = useState<string>('');
  const [copied, setCopied] = useState(false);
  const [isGeneratingQR, setIsGeneratingQR] = useState(false);

  // Memoize UTXO token check
  const isUTXOToken = useMemo(() => 
    ['RVN', 'BTC', 'LTC', 'DOGE', 'BCH', 'ZEC', 'DASH', 'KMD', 'QTUM'].includes(token.toUpperCase()),
    [token]
  );

  // Get provider tracking URL if available
  const getProviderTrackingUrl = (): string | null => {
    if (!exchangeId) return null;
    const providerLower = provider.toLowerCase();
    if (providerLower.includes('simpleswap')) {
      return `https://simpleswap.io/exchange/${exchangeId}`;
    }
    if (providerLower.includes('stealthex')) {
      return `https://stealthex.io/exchange/${exchangeId}`;
    }
    if (providerLower.includes('changenow')) {
      // ChangeNOW uses /exchange/txs/ format for transaction tracking
      return `https://changenow.io/exchange/txs/${exchangeId}`;
    }
    return null;
  };

  // Get estimated confirmation time based on token
  const getEstimatedConfirmationTime = (): string => {
    const upperToken = token.toUpperCase();
    if (upperToken === 'BTC') return '10-60 minutes';
    if (upperToken === 'ETH' || upperToken === 'BNB' || upperToken === 'MATIC') return '1-5 minutes';
    if (['RVN', 'LTC', 'DOGE', 'BCH', 'ZEC', 'DASH', 'KMD', 'QTUM'].includes(upperToken)) return '5-20 minutes';
    if (upperToken === 'XMR') return '20-40 minutes';
    return '5-20 minutes';
  };

  // Get wallet-specific instructions
  const getWalletInstructions = (): string[] => {
    const upperToken = token.toUpperCase();
    const instructions: string[] = [];
    
    if (isUTXOToken) {
      instructions.push(`Open your ${token} wallet (Trust Wallet, NOW Wallet, Electrum, etc.)`);
      instructions.push('Tap "Send" or "Transfer"');
    } else if (upperToken === 'XMR') {
      instructions.push('Open your Monero wallet (Cake Wallet, Monerujo, Monero GUI, etc.)');
      instructions.push('Click "Send"');
    } else {
      instructions.push(`Open your ${token} wallet (MetaMask, Trust Wallet, etc.)`);
      instructions.push('Click "Send" or "Transfer"');
    }
    
    if (qrCodeDataUrl) {
      instructions.push('Scan the QR code above or paste the deposit address');
    } else {
      instructions.push('Paste the deposit address shown above');
    }
    
    instructions.push(`Enter the amount: ${amount} ${token}`);
    instructions.push('Confirm and send the transaction');
    instructions.push(`Wait for blockchain confirmation (usually ${getEstimatedConfirmationTime()})`);
    instructions.push(`Your ${toToken || 'tokens'} will be sent to your destination address automatically`);
    
    return instructions;
  };

  const trackingUrl = getProviderTrackingUrl();

  // Responsive QR code size
  const qrSize = useMemo(() => {
    if (typeof window !== 'undefined') {
      return window.innerWidth < 640 ? 200 : 300;
    }
    return 300;
  }, []);

  useEffect(() => {
    if (open && depositAddress) {
      // Check cache first
      const cached = qrCodeCache.get(depositAddress);
      if (cached) {
        setQrCodeDataUrl(cached);
        setIsGeneratingQR(false);
        return;
      }

      setIsGeneratingQR(true);
      loadQRCode()
        .then((QRCodeLib: any) => {
          return QRCodeLib.toDataURL(depositAddress, {
            width: qrSize,
            margin: 2,
            color: {
              dark: '#000000',
              light: '#FFFFFF',
            },
          });
        })
        .then((url) => {
          // Cache the generated QR code
          qrCodeCache.set(depositAddress, url);
          setQrCodeDataUrl(url);
          setIsGeneratingQR(false);
        })
        .catch((error) => {
          console.error('Failed to generate QR code:', error);
          setIsGeneratingQR(false);
        });
    } else if (!open) {
      // Clear QR code when modal closes to free memory
      setQrCodeDataUrl('');
    }
  }, [open, depositAddress, qrSize]);

  const handleCopy = useCallback(() => {
    navigator.clipboard.writeText(depositAddress);
    setCopied(true);
    toast.success('Address Copied', {
      description: 'Deposit address copied to clipboard',
      duration: 2000,
    });
    setTimeout(() => setCopied(false), 2000);
  }, [depositAddress]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <QrCode className="h-5 w-5" />
            Send {token} to Complete Swap
          </DialogTitle>
          <DialogDescription>
            Send {amount} {token} to the deposit address below to complete your swap via {provider}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          {/* Important Alert */}
          <Alert className="border-yellow-500/50 bg-yellow-500/10">
            <AlertCircle className="h-4 w-4 text-yellow-400" />
            <AlertDescription className="text-sm">
              <strong>Important:</strong> Send exactly <strong>{amount} {token}</strong> to this address. 
              {isUTXOToken && ' The swap will complete automatically once your payment is confirmed on the blockchain.'}
              <br />
              <span className="text-xs mt-1 block">
                Estimated confirmation time: <strong>{getEstimatedConfirmationTime()}</strong>
              </span>
            </AlertDescription>
          </Alert>

          {/* QR Code */}
          <div className="flex flex-col items-center gap-4 p-6 bg-muted/50 rounded-lg border">
            {isGeneratingQR ? (
              <div className="flex flex-col items-center gap-2 py-8">
                <Loader2 className="h-8 w-8 animate-spin text-primary" />
                <p className="text-sm text-muted-foreground">Generating QR code...</p>
              </div>
            ) : qrCodeDataUrl ? (
              <div className="flex flex-col items-center gap-3">
                <img 
                  src={qrCodeDataUrl} 
                  alt="Deposit Address QR Code" 
                  className="w-64 h-64 border-4 border-background rounded-lg shadow-lg"
                />
                <p className="text-xs text-muted-foreground text-center">
                  Scan with your wallet to send {token}
                </p>
              </div>
            ) : (
              <div className="flex flex-col items-center gap-2 py-8">
                <AlertCircle className="h-8 w-8 text-muted-foreground" />
                <p className="text-sm text-muted-foreground">QR code unavailable</p>
              </div>
            )}
          </div>

          {/* Deposit Address */}
          <div className="space-y-2">
            <Label className="text-sm font-medium flex items-center gap-2">
              Deposit Address
              <span className="text-xs font-normal text-muted-foreground">
                (from {provider})
              </span>
            </Label>
            <div className="flex gap-2">
              <Input
                value={depositAddress}
                readOnly
                className="font-mono text-sm bg-muted/50"
                title={`Deposit address from ${provider}. Send exactly ${amount} ${token} to this address.`}
              />
              <Button
                variant="outline"
                size="icon"
                onClick={handleCopy}
                className="shrink-0"
                title="Copy deposit address"
              >
                {copied ? (
                  <CheckCircle2 className="h-4 w-4 text-green-500" />
                ) : (
                  <Copy className="h-4 w-4" />
                )}
              </Button>
            </div>
            <div className="flex items-center justify-between">
              <p className="text-xs text-muted-foreground">
                Copy this address and send <strong>{amount} {token}</strong> from your wallet
              </p>
              <Badge variant="outline" className="text-xs">
                {provider}
              </Badge>
            </div>
          </div>

          {/* Exchange ID (if available) */}
          {exchangeId && (
            <div className="space-y-2">
              <Label className="text-sm font-medium">Exchange ID</Label>
              <div className="flex gap-2">
                <Input
                  value={exchangeId}
                  readOnly
                  className="font-mono text-sm"
                />
                <Button
                  variant="outline"
                  size="icon"
                  onClick={() => {
                    navigator.clipboard.writeText(exchangeId);
                    toast.success('Exchange ID Copied');
                  }}
                >
                  <Copy className="h-4 w-4" />
                </Button>
                {trackingUrl && (
                  <Button
                    variant="outline"
                    size="icon"
                    onClick={() => window.open(trackingUrl, '_blank')}
                    title="Track swap status on provider website"
                  >
                    <ExternalLink className="h-4 w-4" />
                  </Button>
                )}
              </div>
              <div className="flex items-center justify-between">
                <p className="text-xs text-muted-foreground">
                  Keep this ID to track your swap status
                </p>
                {trackingUrl && (
                  <Button
                    variant="link"
                    size="sm"
                    className="text-xs h-auto p-0"
                    onClick={() => window.open(trackingUrl, '_blank')}
                  >
                    View Status on {provider} →
                  </Button>
                )}
              </div>
            </div>
          )}

          {/* Memo/Tag (if available - required for XRP, XLM, etc.) */}
          {payinExtraId && (
            <Alert className="border-orange-500/50 bg-orange-500/10">
              <AlertCircle className="h-4 w-4 text-orange-400" />
              <AlertDescription className="text-sm">
                <div className="space-y-2">
                  <div>
                    <strong className="block mb-1">Memo/Tag Required:</strong>
                    <span className="text-xs text-muted-foreground block mb-2">
                      For {token} swaps, you MUST include this memo/tag when sending funds, otherwise your funds may be lost.
                    </span>
                  </div>
                  <div className="flex gap-2">
                    <Input
                      value={payinExtraId}
                      readOnly
                      className="font-mono text-sm bg-background"
                    />
                    <Button
                      variant="outline"
                      size="icon"
                      onClick={() => {
                        navigator.clipboard.writeText(payinExtraId);
                        toast.success('Memo/Tag Copied');
                      }}
                    >
                      <Copy className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              </AlertDescription>
            </Alert>
          )}

          {/* Destination Address (for cross-chain) */}
          {destinationAddress && toToken && (
            <Alert className="border-blue-500/50 bg-blue-500/10">
              <Info className="h-4 w-4 text-blue-400" />
              <AlertDescription className="text-sm">
                <strong>You will receive:</strong> {toToken} will be sent to{' '}
                <span className="font-mono text-xs">{destinationAddress.substring(0, 10)}...{destinationAddress.slice(-8)}</span>
              </AlertDescription>
            </Alert>
          )}

          {/* Step-by-Step Instructions */}
          <div className="space-y-3 p-4 bg-muted/30 rounded-lg border">
            <h4 className="text-sm font-semibold">How to Complete Your Swap:</h4>
            <ol className="list-decimal list-inside space-y-2 text-sm text-muted-foreground">
              {getWalletInstructions().map((instruction, index) => (
                <li key={index}>{instruction}</li>
              ))}
            </ol>
          </div>

          {/* Provider Info */}
          <Alert className="border-purple-500/50 bg-purple-500/10">
            <Info className="h-4 w-4 text-purple-400" />
            <AlertDescription className="text-sm">
              <strong>Provider:</strong> {provider}
              {exchangeId && (
                <> • <strong>Exchange ID:</strong> {exchangeId.substring(0, 12)}...</>
              )}
              <br />
              <span className="text-xs">
                The swap is processed by {provider}. Your funds are secure and will be automatically exchanged once received.
              </span>
            </AlertDescription>
          </Alert>

          {/* Action Buttons */}
          <div className="flex gap-2 pt-4">
            <Button
              variant="outline"
              onClick={() => onOpenChange(false)}
              className="flex-1"
            >
              I'll Send Later
            </Button>
            <Button
              onClick={handleCopy}
              className="flex-1"
            >
              {copied ? 'Copied!' : 'Copy Address'}
            </Button>
          </div>

          {/* Warning */}
          <Alert variant="destructive" className="border-red-500/50 bg-red-500/10">
            <AlertCircle className="h-4 w-4 text-red-400" />
            <AlertDescription className="text-xs">
              <strong>Warning:</strong> Only send {token} to this address. Sending other cryptocurrencies will result in permanent loss of funds.
            </AlertDescription>
          </Alert>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// Export component (memo removed to avoid initialization issues)
export default DepositAddressModal;
